import { useState, useEffect } from 'react'
import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { useCurrencyStore } from '@/store/currency.store'

/** Merge Tailwind classes with clsx — handles conflicts correctly */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/** Responsive hook: true when viewport < 768px */
export function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  return isMobile
}

// ── Currency Formatting ──────────────────────────────────────────────

/** Compact NGN format: ₦1.2M, ₦50k, ₦500 */
export function formatNGN(kobo: number): string {
  const naira = kobo / 100
  if (naira >= 1_000_000) return `₦${(naira / 1_000_000).toFixed(1)}M`
  if (naira >= 1_000) return `₦${(naira / 1_000).toFixed(0)}k`
  return `₦${naira.toLocaleString('en-NG')}`
}

/** Full precision NGN format: ₦1,200,000 */
export function formatNGNFull(kobo: number): string {
  return '₦' + (kobo / 100).toLocaleString('en-NG', { minimumFractionDigits: 0 })
}

/**
 * Dual Currency Formatter
 * Formats a kobo value into both NGN and USD based on user preference and live rates.
 */
export function formatCurrency(
  kobo: number,
  options: {
    showDual?: boolean
    compact?: boolean
    primaryOnly?: boolean
  } = {}
): string {
  const { showDual = true, compact = false, primaryOnly = false } = options
  const { preferredCurrency, exchangeRate } = useCurrencyStore.getState()

  const naira = kobo / 100
  const usd = naira * exchangeRate

  const formatNaira = (val: number) => {
    if (compact) {
      if (val >= 1_000_000) return `₦${(val / 1_000_000).toFixed(1)}M`
      if (val >= 1_000) return `₦${(val / 1_000).toFixed(0)}k`
    }
    return `₦${val.toLocaleString('en-NG', { maximumFractionDigits: 0 })}`
  }

  const formatUSD = (val: number) => {
    if (compact) {
      if (val >= 1_000_000) return `$${(val / 1_000_000).toFixed(1)}M`
      if (val >= 1_000) return `$${(val / 1_000).toFixed(1)}k`
    }
    return `$${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }

  const primaryStr = preferredCurrency === 'NGN' ? formatNaira(naira) : formatUSD(usd)
  const secondaryStr = preferredCurrency === 'NGN' ? formatUSD(usd) : formatNaira(naira)

  if (primaryOnly) return primaryStr
  if (!showDual) return primaryStr

  return `${primaryStr} (${secondaryStr})`
}

// ── Financial Helpers ────────────────────────────────────────────────

/** Stamp duty: ₦50 (5000 kobo) on transactions ≥ ₦10,000 (1_000_000 kobo) */
export function stampDuty(kobo: number): number {
  return kobo >= 1_000_000 ? 5000 : 0
}

// ── Date Formatting ──────────────────────────────────────────────────

export function formatRelativeDate(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = date.getTime() - now.getTime()
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24))
  if (diffDays < 0) return 'Past'
  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Tomorrow'
  if (diffDays < 7) return `In ${diffDays} days`
  return date.toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' })
}

export function formatEventDate(startAt: string, endAt: string): string {
  const start = new Date(startAt)
  const end = new Date(endAt)
  const startStr = start.toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' })
  if (start.toDateString() === end.toDateString()) {
    return startStr
  }
  return `${startStr} – ${end.toLocaleDateString('en-NG', { day: 'numeric', month: 'short' })}`
}

export function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString('en-NG', { hour: '2-digit', minute: '2-digit', hour12: true })
}

export function formatTxDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' })
}

// ── Misc Helpers ─────────────────────────────────────────────────────

export function percentSold(sold: number, capacity: number): number {
  if (capacity === 0) return 0
  return Math.round((sold / capacity) * 100)
}
